import { Box, Button, Stack, Typography, useTheme } from '@mui/material';
import { ArrowRight, ClipboardCheck } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { patientApi } from '../../lib/api';
import type { PatientPendingCheckin } from '../../lib/api/modules/patient-portal';
import { design } from '../../theme/clinveaDesign';
import { formatLocaleDate } from '../../i18n/format';
import { parseLocalDate } from '../../lib/utils';
import { withDr } from '../../lib/doctorName';
import { to12h } from './bookingUtils';

export const PENDING_CHECKINS_KEY = ['patient-checkins-pending'] as const;

/** Check-in forms the clinic sent that are still waiting (medopd-docs 140). Shared by the dashboard, appointments and sidebar. */
export function usePendingCheckins() {
  return useQuery({
    queryKey: PENDING_CHECKINS_KEY,
    queryFn: async () => {
      const res = await patientApi.checkins.pending();
      if (res.error) throw new Error(res.error);
      return (res.data ?? []) as PatientPendingCheckin[];
    },
    staleTime: 30_000,
  });
}

/**
 * "Action needed" card: each check-in form the clinic sent, with the visit it is for and a button that opens the form
 * inside the portal. Renders nothing when no form is waiting.
 */
export function PendingCheckinsCard() {
  const { t, i18n } = useTranslation('portal');
  const theme = useTheme();
  const d = design(theme.palette.mode);
  const { data = [] } = usePendingCheckins();
  if (data.length === 0) return null;

  const when = (c: PatientPendingCheckin) => [
    c.date ? formatLocaleDate(parseLocalDate(c.date), i18n.language, { weekday: 'short', day: 'numeric', month: 'short' }) : null,
    c.time ? to12h(c.time) : null,
  ].filter(Boolean).join(' · ');

  return (
    <Box role="region" aria-label={t('ciActionNeeded', 'Action needed')}
      sx={{ mb: 3, borderRadius: 3, border: '1px solid', borderColor: d.amber, bgcolor: d.amberTint, overflow: 'hidden' }}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ p: { xs: 2, sm: 2.25 } }}>
        <Box sx={{ width: 40, height: 40, borderRadius: 2, display: 'grid', placeItems: 'center', bgcolor: d.amber, color: '#fff', flexShrink: 0 }}>
          <ClipboardCheck size={22} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="overline" sx={{ color: d.amber, fontWeight: 800, lineHeight: 1.4 }}>{t('ciActionNeeded', 'Action needed')}</Typography>
          <Typography sx={{ fontWeight: 800, fontSize: '1.05rem' }}>
            {t('ciCardTitle', { count: data.length })}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {t('ciCardBody', 'Your clinic asked you to fill this before your visit. It takes a few minutes and saves time at the front desk.')}
          </Typography>
        </Box>
      </Stack>
      <Stack sx={{ bgcolor: d.surface }} divider={<Box sx={{ borderTop: '1px solid', borderColor: d.lineSoft }} />}>
        {data.map((c) => (
          <Stack key={c.checkinId} direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ sm: 'center' }} justifyContent="space-between"
            sx={{ px: { xs: 2, sm: 2.25 }, py: 1.5 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {c.doctorName ? t('ciVisitWith', { defaultValue: 'Visit with {{doctor}}', doctor: withDr(c.doctorName) }) : t('ciYourVisit', 'Your visit')}
              </Typography>
              <Typography variant="caption" color="text.secondary">{when(c)}</Typography>
            </Box>
            <Button component={RouterLink} to={`/patient/checkin/${encodeURIComponent(c.checkinId)}`} variant="contained"
              endIcon={<ArrowRight size={16} />}
              sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, bgcolor: d.amber, '&:hover': { bgcolor: '#92400e' },
                alignSelf: { xs: 'stretch', sm: 'center' }, whiteSpace: 'nowrap' }}>
              {c.status === 'IN_PROGRESS' ? t('ciContinue', 'Continue check-in form') : t('ciFill', 'Fill check-in form')}
            </Button>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}
